"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StepProps, BaseFormData } from "../components/types";
import { ValidationRules } from "../components/FormValidation";
import parse from "html-react-parser";
import { cn } from "@/lib/utils";
import { useFormContext, useWatch } from "react-hook-form";
import { FormField, FormItem, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { 
  getStatesForCountry, 
  shouldUseTextInput,
  baseFormOptions,
  getStateFieldLabel, 
  // getStateFieldPlaceholder 
} from "./baseFormOptions";
import { Card, CardContent } from "@/components/ui/card";

export interface TurnoverAlertConfig {
  // Turnover ranges that trigger the alert (e.g., "$0 - $999,999")
  triggerValues: string[];
  // Alert message content (supports HTML)
  message: string;
  // Alert variant style
  className?: '';
}

export interface BusinessDetailsStepConfig {
  showTradingName?: boolean;
  showCountryOfRegistration?: boolean;
  showState?: boolean;
  showBusinessType?: boolean;
  showIndustry?: boolean;
  showNumberOfEmployees?: boolean;
  showNumberOfSuppliers?: boolean;
  showNumberOfCustomers?: boolean;
  showOutstandingInvoiceValue?: boolean;
  showNetAssets?: boolean;
  showAnnualTurnover?: boolean;
  showAnnualOptionalTurnover?: boolean;
  showBestDescribesYou?: boolean;
  showNumberOfClients?: boolean;
  showBusinessDescription?: boolean;
  // Optional alert configuration for annual turnover
  turnoverAlert?: TurnoverAlertConfig;
  // Custom countries list (if not provided, uses formOptions.countries)
  countries?: string[];
}

type BusinessDetailsStepProps<T = BaseFormData> = StepProps<T>;

export const BusinessDetailsStep = <T extends BaseFormData = BaseFormData>(props: BusinessDetailsStepProps<T>) => {
  const { title, description, formOptions } = props;
  const { control, setValue, formState } = useFormContext();
  
  // Watch country field for dynamic state selection
  const selectedCountry = useWatch({ 
    control, 
    name: "countryOfRegistration" as never 
  }) as unknown as string | undefined;

  // Watch annual turnover field for alert triggering
  const selectedTurnover = useWatch({
    control,
    name: "annualTurnover" as never
  }) as unknown as string | undefined;

  // Watch industry field for conditional "Other" input
  const selectedIndustry = useWatch({
    control,
    name: "industry" as never
  }) as unknown as string | undefined;

  // Extract stepConfig with type safety
  const stepConfig = props.stepConfig as BusinessDetailsStepConfig | undefined;

  // Default configuration - show all fields if no config provided
  const config = stepConfig || {
    showTradingName: false,
    showCountryOfRegistration: false,
    showState: false,
    showBusinessType: false,
    showIndustry: false,
    showNumberOfEmployees: false,
    showNumberOfSuppliers: false,
    showNumberOfCustomers: false,
    showOutstandingInvoiceValue: false,
    showNetAssets: false,
    showAnnualTurnover: false,
    showAnnualOptionalTurnover: false,
    showBestDescribesYou: false,
    showNumberOfClients: false,
    showBusinessDescription: false,
  };

  // formOptions is always provided via createFormOptions() 
  const countries = config.countries || formOptions.countries;
  const states = baseFormOptions.states; // Use legacy states directly
  
  // Dynamic state logic based on selected country
  const dynamicStates = selectedCountry ? getStatesForCountry(selectedCountry) : states;
  const useTextInput = selectedCountry ? shouldUseTextInput(selectedCountry) : false;
  const stateFieldLabel = selectedCountry ? getStateFieldLabel(selectedCountry) : "State";
  // const stateFieldPlaceholder = selectedCountry ? getStateFieldPlaceholder(selectedCountry) : "Select";
  
  // Clear state only when country actually changes to a different value
  const prevCountryRef = React.useRef<string | undefined>(undefined);
  
  // Track display value for invoice input formatting
  // const [invoiceDisplayValue, setInvoiceDisplayValue] = React.useState("");
  
  // Initialize display value from form value
  // React.useEffect(() => {
  //   const invoiceValue = control._formValues?.outstandingInvoiceValue;
  //   if (invoiceValue) {
  //     const formatted = Number(invoiceValue).toLocaleString(undefined, { 
  //       minimumFractionDigits: 0, 
  //       maximumFractionDigits: 2 
  //     });
  //     setInvoiceDisplayValue(formatted);
  //   }
  // }, [control._formValues?.outstandingInvoiceValue]);
  React.useEffect(() => {
    if (selectedCountry && prevCountryRef.current && prevCountryRef.current !== selectedCountry) {
      setValue("state", "");
    }
    prevCountryRef.current = selectedCountry;
  }, [selectedCountry, setValue]);
  const businessTypes = formOptions.businessTypes;
  const industries = formOptions.industries;
  const employeeRanges = formOptions.employeeRanges;
  const customerRanges = formOptions.customerRanges;
  const turnoverRanges = formOptions.turnoverRanges;
  const supplierRanges = formOptions.supplierRanges;
  const netAssetsRanges = formOptions.netAssetsRanges;
  const bestDescribesYouOptions = formOptions.bestDescribesYouOptions;
  // const numberOfClientsRanges = formOptions.numberOfClientsRanges;
  const invoiceRanges = formOptions.invoiceRanges;
  // Check if alert should be shown based on turnover selection
  const shouldShowAlert = React.useMemo(() => {
    if (!config.turnoverAlert || !selectedTurnover) {
      return false;
    }
    return config.turnoverAlert.triggerValues.includes(selectedTurnover);
  }, [config.turnoverAlert, selectedTurnover]);

  return (
    <div className="flex flex-col gap-12">
      {/* Main Title */}
      <div className="flex flex-col gap-4">
        <h3>
          {title}
        </h3>
        {description && (
          <p className="paragraph-lg text-muted-foreground [&_strong]:text-foreground">
            {parse(description)}
          </p>
        )}
      </div>

      {/* Form Fields */}
      <div className="flex flex-col gap-6">
        {/* Trading Name (Optional) */}
        {config.showTradingName && (
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name={"tradingName" as never}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trading Name <span className="font-normal text-muted-foreground">(Optional)</span></FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={(field.value as string) || ""}
                      onChange={(e) => { field.onChange(e); }}
                      maxLength={300}
                      placeholder=""
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <p className="paragraph-sm text-muted-foreground">If different from your registered business name.</p>
          </div>
        )}

        {(config.showCountryOfRegistration || config.showState) && (
          <div className="grid md:grid-cols-2 gap-6 md:gap-4">
            {/* Country of Registration */}
            {config.showCountryOfRegistration && (
              <div className="flex flex-col gap-2">
                            <FormField
                control={control}
                name={"countryOfRegistration" as never}
                rules={ValidationRules.selection("country")}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country of Registration</FormLabel>
                    <FormControl>
                      <Select
                        value={(field.value as string) || ""}
                        onValueChange={(value) => { field.onChange(value); }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country: string) => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              </div>
            )}

            {/* State - Dynamic based on country selection */}
            {config.showState && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"state" as never}
                  rules={{
                    required: selectedCountry && selectedCountry !== "Other"
                      ? (shouldUseTextInput(selectedCountry) 
                          ? `Please enter a ${stateFieldLabel.toLowerCase()}`
                          : `Please select a ${stateFieldLabel.toLowerCase()}`)
                      : false,
                    validate: (value: unknown) => {
                      if (selectedCountry === "Other") {
                        return true; // Optional when country is "Other"
                      }
                      // When country is not "Other", check if value is selected
                      const stringValue = typeof value === 'string' ? value : String(value || '');
                      if (!value || stringValue.trim() === '') {
                        return shouldUseTextInput(selectedCountry || '')
                          ? `Please enter a ${stateFieldLabel.toLowerCase()}`
                          : `Please select a ${stateFieldLabel.toLowerCase()}`;
                      }
                      return true; // Valid if value is selected
                    }
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{stateFieldLabel} {selectedCountry === "Other" && <span className="font-normal text-muted-foreground">(Optional)</span>}</FormLabel>
                      <FormControl>
                        {useTextInput ? (
                          <Input
                            placeholder=""
                            value={(field.value as string) || ""}
                            onChange={field.onChange}
                            maxLength={100}
                          />
                        ) : (
                          <Select
                            value={(field.value as string) || ""}
                            onValueChange={(value) => { field.onChange(value); }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {dynamicStates.map((s: { code: string; name: string }) => (
                                <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {(config.showBusinessType || config.showIndustry) && (
          <div className={cn(
            "grid gap-6 md:gap-4", 
            (config.showIndustry && config.showBusinessType) ? "md:grid-cols-2" : "md:grid-cols-1")}
          >
            {/* Business Type */}
            {config.showBusinessType && (
              <div className="flex flex-col gap-2">
                            <FormField
                control={control}
                name={"businessType" as never}
                rules={ValidationRules.selection("business type")}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Type</FormLabel>
                    <FormControl>
                      <Select
                        value={(field.value as string) || ""}
                        onValueChange={(value) => { field.onChange(value); }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {businessTypes.map((type: string) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              </div>
            )}

            {/* Industry */}
            {config.showIndustry && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"industry" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry</FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map((industry: string) => (
                              <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Custom Industry Input - shown when "Other" is selected in Industry */}
            {config.showIndustry && selectedIndustry === "Other" && (
              <div className="flex flex-col gap-2 md:col-span-2">
                <FormField
                  control={control}
                  name={"customIndustry" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Enter your industry</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={(field.value as string) || ""}
                          onChange={(e) => { field.onChange(e); }}
                          placeholder=""
                          maxLength={500}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {(config.showNumberOfEmployees || config.showNumberOfSuppliers || config.showNumberOfCustomers) && (
          <div className="grid md:grid-cols-2 gap-6 md:gap-4">
            {/* Number of Employees */}
            {config.showNumberOfEmployees && (
              <div className="flex flex-col gap-2"> 
                <FormField
                  control={control}
                  name={"numberOfEmployees" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Number of Employees</FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {employeeRanges.map((range: string) => (
                              <SelectItem key={range} value={range}>{range}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Number of Suppliers (Optional) */}
            {config.showNumberOfSuppliers && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"numberOfSuppliers" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Number of B2B Suppliers <span className="font-normal text-muted-foreground">(Optional)</span></FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {supplierRanges.map((range: string) => (
                              <SelectItem key={range} value={range}>{range}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}
          
            {/* Number of Customers */}
            {config.showNumberOfCustomers && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"numberOfCustomers" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Number of B2B Customers <span className="font-normal text-muted-foreground"></span></FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {customerRanges.map((range: string) => (
                              <SelectItem key={range} value={range}>{range}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {(config.showOutstandingInvoiceValue || config.showNetAssets || config.showAnnualTurnover || config.showAnnualOptionalTurnover) && (
          <div className="grid md:grid-cols-2 gap-6 md:gap-4">
            {/* Outstanding Invoice Value */}
            {config.showOutstandingInvoiceValue && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"outstandingInvoiceValue" as never}
                  // rules={ValidationRules.positiveNumber()}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Approx. Unpaid Invoice Value <span className="font-normal text-muted-foreground"></span></FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {invoiceRanges.map((range: string) => (
                              <SelectItem key={range} value={range}>{range}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    {/* <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 ">$</span>
                        <Input
                          type="text"
                          value={invoiceDisplayValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Remove all non-digit and non-decimal characters
                            const cleanValue = value.replace(/[^\d.]/g, '');
                            // Ensure only one decimal point
                            const parts = cleanValue.split('.');
                            let finalValue = parts[0];
                            if (parts.length > 1) {
                              // Limit to 2 decimal places
                              finalValue += '.' + parts[1].substring(0, 2);
                            }
                            
                            // Format with commas for display
                            const numericPart = finalValue.split('.')[0];
                            const decimalPart = finalValue.split('.')[1];
                            const formattedNumeric = numericPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                            const displayValue = decimalPart !== undefined ? formattedNumeric + '.' + decimalPart : formattedNumeric;
                            
                            setInvoiceDisplayValue(displayValue);
                            field.onChange(parseFloat(finalValue) || 0);
                          }}
                          // onFocus={() => {
                          //   // Show clean value when focused for easier editing
                          //   const cleanValue = field.value ? String(field.value) : "";
                          //   setInvoiceDisplayValue(cleanValue);
                          // }}
                          onBlur={() => {
                            // Show formatted value when blurred
                            if (field.value) {
                              const formatted = Number(field.value).toLocaleString(undefined, { 
                                minimumFractionDigits: 0, 
                                maximumFractionDigits: 2 
                              });
                              setInvoiceDisplayValue(formatted);
                            }
                          }}
                          placeholder=""
                          className="pl-8"
                        />
                      </div>
                    </FormControl>
                    <FormMessage /> */}
                  </FormItem>
                )}
              />
              </div>
            )}
        
            {/* Annual Turnover */}
            {(config.showAnnualTurnover || config.showAnnualOptionalTurnover) && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"annualTurnover" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Approx. Annual Turnover {config.showAnnualOptionalTurnover && <span className="font-normal text-muted-foreground">(Optional)</span>}</FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {turnoverRanges.map((range: string) => (
                              <SelectItem key={range} value={range}>{range}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Approx. Net Assets */}
            {config.showNetAssets && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"netAssets" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Approx. Net Assets <span className="font-normal text-muted-foreground">(Optional)</span></FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {netAssetsRanges.map((range: string) => (
                              <SelectItem key={range} value={range}>{range}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        )}
        
        {/* Best Describes You and Number of Clients */}
        {(config.showBestDescribesYou || config.showNumberOfClients) && (
          <div className="grid gap-6 md:gap-4">
            {/* Best Describes You */}
            {config.showBestDescribesYou && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"bestDescribesYou" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your role / business type</FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {bestDescribesYouOptions.map((option: string) => (
                              <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Number of Clients */}
            {/* {config.showNumberOfClients && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"numberOfClients" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Approx. Number of Clients <span className="font-normal text-muted-foreground">(Optional)</span></FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {numberOfClientsRanges.map((range: string) => (
                              <SelectItem key={range} value={range}>{range}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )} */}
          </div>
        )}
        
        {/* Business Description */}
        {config.showBusinessDescription && (
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name={"businessDescription" as never}
              render={({ field }) => {
                const businessDescriptionValue = (field.value as string) || "";
                const characterCount = businessDescriptionValue.length;
                const maxLength = 4000;
                
                return (
                  <FormItem>
                    <FormLabel>Describe your referral business <span className="font-normal text-muted-foreground">(Optional)</span></FormLabel>
                    <FormControl>
                      <Textarea 
                        {...field}
                        value={businessDescriptionValue}
                        onChange={(e) => { field.onChange(e); }}
                        placeholder=""
                        rows={6}
                        maxLength={maxLength}
                      />
                    </FormControl>
                    <div className="flex justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {formState.errors.businessDescription ? (
                          <FormMessage />
                        ) : (
                          <p className="paragraph-sm text-muted-foreground">
                            The more you share, the easier it is for us to connect you with the right BDM.
                          </p>
                        )}
                      </div>
                      <p className="paragraph-sm text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {characterCount} / {maxLength}
                      </p>
                    </div>
                  </FormItem>
                );
              }}
            />
          </div>
        )}

        {/* State / Region */}
        { selectedCountry === "Other" && (
          <Card className="bg-gray-100 p-0 border-0">
            <CardContent className="py-4 px-6 paragraph-sm text-muted-foreground [&_strong]:text-foreground">
              <p><strong>Please note:</strong> Octet currently only provides services to businesses in the listed countries. If your business is elsewhere, you&apos;re still welcome to submit your enquiry and we&apos;ll let you know if we can help.</p>
            </CardContent>
          </Card>
        )}

        {/* Turnover Alert - Conditionally shown based on configuration and selection */}
        {shouldShowAlert && config.turnoverAlert && (
          <Card className={config.turnoverAlert.className}>
            <CardContent className="py-4 px-6 paragraph-sm text-muted-foreground [&_strong]:text-foreground">
              <p>{parse(config.turnoverAlert.message)}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Button removed - now handled by centralized submit button */}
    </div>
  );
}; 